import { dataClient } from '@/services/api/api-client'
import { TickerSearchResultSchema, type TickerSearchResult } from '@/types/schemas/api'

/**
 * In-memory ticker directory for instant client-side search.
 *
 * Loaded once after auth, refreshed every hour. The full US equities list
 * is ~8-10K entries (~60-80KB gzipped) — small enough to hold in memory
 * and filter synchronously.
 */

let directory: TickerSearchResult[] = []
let refreshTimer: ReturnType<typeof setInterval> | null = null
let retryTimer: ReturnType<typeof setTimeout> | null = null
let fetchFn: (() => Promise<TickerSearchResult[]>) | null = null

const REFRESH_INTERVAL = 60 * 60 * 1000 // 1 hour
// A failed (or empty) load tries again this soon: until it lands, every search goes to the
// provider fallback, a network round trip per keystroke, ranked by the provider.
const RETRY_INTERVAL = 30 * 1000

/** Initialize the directory with a fetch function and load immediately. */
export function initTickerDirectory(fetch: () => Promise<TickerSearchResult[]>) {
  fetchFn = fetch
  refresh()
  refreshTimer = setInterval(refresh, REFRESH_INTERVAL)
}

/** Stop the hourly refresh (call on logout / unmount). */
export function destroyTickerDirectory() {
  if (refreshTimer) clearInterval(refreshTimer)
  refreshTimer = null
  if (retryTimer) clearTimeout(retryTimer)
  retryTimer = null
  fetchFn = null
  directory = []
}

async function refresh() {
  const load = fetchFn
  if (!load) return
  if (retryTimer) clearTimeout(retryTimer)
  retryTimer = null
  try {
    const next = await load()
    if (fetchFn !== load) return // logged out (or re-initialized) while loading
    // The list is thousands of symbols; an empty one is a failed load (the caller maps a
    // request error to []), not a market with no tickers.
    if (next.length === 0) throw new Error('empty ticker directory')
    directory = next
  } catch {
    // Keep the last good list, and try again soon rather than at the hourly refresh.
    if (fetchFn === load) retryTimer = setTimeout(refresh, RETRY_INTERVAL)
  }
}

/** Search tickers by prefix match on symbol or substring match on name. */
export function searchTickers(query: string, limit = 15): TickerSearchResult[] {
  if (!query) return []
  const q = query.toUpperCase()
  const results: TickerSearchResult[] = []

  // Exact match first
  for (const t of directory) {
    if (t.symbol === q) {
      results.push(t)
      break
    }
  }

  // Prefix matches on symbol
  for (const t of directory) {
    if (results.length >= limit) break
    if (t.symbol !== q && t.symbol.startsWith(q)) {
      results.push(t)
    }
  }

  // Substring matches on name (fill remaining slots)
  if (results.length < limit) {
    const nameQ = q.toLowerCase()
    for (const t of directory) {
      if (results.length >= limit) break
      if (!results.includes(t) && t.name.toLowerCase().includes(nameQ)) {
        results.push(t)
      }
    }
  }

  return results
}

/** Search the local directory first, then discover symbols missing locally. */
export async function searchTickersWithFallback(
  query: string,
  limit = 15,
): Promise<TickerSearchResult[]> {
  const normalizedQuery = query.trim().toUpperCase()
  if (!normalizedQuery) return []

  const localResults = searchTickers(normalizedQuery, limit)
  if (localResults.length > 0) return localResults

  try {
    const data = await dataClient.get<unknown>(
      `/v2/tickers/search?query=${encodeURIComponent(normalizedQuery)}`,
    )
    return rankBySymbol(TickerSearchResultSchema.array().parse(data), normalizedQuery).slice(
      0,
      limit,
    )
  } catch {
    return []
  }
}

/**
 * The exact symbol first, then symbols that start with the query, then the rest in the
 * provider's order (it lists alphabetically, so AAPB, a 2x AAPL ETF, came before AAPL).
 * The same order `searchTickers` gives local results.
 */
function rankBySymbol(results: TickerSearchResult[], query: string): TickerSearchResult[] {
  const tier = (symbol: string) => (symbol === query ? 0 : symbol.startsWith(query) ? 1 : 2)
  return [...results].sort((a, b) => tier(a.symbol.toUpperCase()) - tier(b.symbol.toUpperCase()))
}

/** Check if the directory has been loaded. */
export function isDirectoryLoaded(): boolean {
  return directory.length > 0
}
